import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCmsSections } from '../cms/useCms';
import { getLocalizedText } from '../cms/localization';
import type { SupportedLang } from '../cms/localization';
import type { LocalizedText } from '../../shared/types';

const STORAGE_KEY = 'fyt_hide_belt_popup';
const SESSION_KEY = 'fyt_popup_shown_this_session';

const DEFAULT_BENEFITS = [
  '免刮痧，全天候被動理療，不佔用你的時間',
  '不只顧腰，肩頸、腹部、關節都能用',
  '出門、辦公、睡覺都能戴著，理療融入日常',
];

const DEFAULT_PRODUCT_PATH =
  '/products/%E5%BE%A1%E9%99%BD%E8%BF%91%E7%B4%85%E5%A4%96%E7%B7%9A%E7%A0%AD%E7%9F%B3%E8%85%B0%E5%B8%B6%EF%BC%88%E7%B2%89%E7%B4%85%E8%89%B2%EF%BC%89';

const DEFAULT_IMAGE =
  'https://fuyangtong-api.jimsbond007.workers.dev/api/public/assets/media/81a5cfc0-774a-4772-b952-6e7ba0f89e63.webp';

export default function ProductRecommendPopup() {
  const { i18n } = useTranslation();
  const lang = i18n.language as SupportedLang;
  const location = useLocation();
  const { sections } = useCmsSections();

  const popupSection = sections.find((s) => s.key === 'popup');
  const content = popupSection?.isActive ? popupSection.contentJson : null;

  const isActive = (content?.isActive as boolean) ?? true;
  const delaySeconds = (content?.delaySeconds as number) ?? 15;
  const productImage = (content?.productImage as string) || DEFAULT_IMAGE;
  const productName = getLocalizedText(
    content?.productName,
    lang,
    '御陽近紅外線砭石腰帶（粉紅色）'
  );
  const eyebrow = getLocalizedText(
    content?.eyebrow,
    lang,
    '扶陽堂 · 御陽砭石系列'
  );
  const tagline = getLocalizedText(
    content?.tagline,
    lang,
    '免刮痧，戴著就在調理'
  );
  const benefitsRaw = (content?.benefits as LocalizedText[]) ||
    DEFAULT_BENEFITS.map((b) => ({
      'zh-HK': b,
      'zh-CN': b,
      en: b,
    }));
  const benefits = benefitsRaw.map((b) => getLocalizedText(b, lang));
  const ctaText = getLocalizedText(
    content?.ctaText,
    lang,
    '立即查看產品 →'
  );
  const ctaHref = (content?.ctaHref as string) || DEFAULT_PRODUCT_PATH;
  const price = (content?.price as string) || '';
  const excludePaths = (content?.excludePaths as string[]) || [
    DEFAULT_PRODUCT_PATH,
  ];

  const [visible, setVisible] = useState(false);
  const [entered, setEntered] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  // 排程：延遲秒數後嘗試彈出；同一 session 只彈一次
  useEffect(() => {
    if (!isActive || typeof window === 'undefined') return;

    const alreadyHidden = window.localStorage.getItem(STORAGE_KEY) === 'true';
    if (alreadyHidden) return;

    const alreadyShownThisSession = window.sessionStorage.getItem(SESSION_KEY) === 'true';
    if (alreadyShownThisSession) return;

    const currentPath = decodeURIComponent(location.pathname);
    const onExcludedPage = excludePaths.some(
      (p) =>
        location.pathname === p ||
        currentPath === decodeURIComponent(p)
    );
    if (onExcludedPage) return;

    const delayMs = Math.max(0, delaySeconds) * 1000;
    const timer = setTimeout(() => {
      setVisible(true);
      window.sessionStorage.setItem(SESSION_KEY, 'true');
    }, delayMs);
    return () => clearTimeout(timer);
  }, [isActive, delaySeconds, excludePaths, location.pathname]);

  // 進場動畫
  useEffect(() => {
    if (!visible) return undefined;
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, [visible]);

  // Esc 關閉 + 鎖背景捲動
  useEffect(() => {
    if (!visible) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [visible]);

  const handleClose = () => {
    if (dontShowAgain && typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, 'true');
    }
    setEntered(false);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="fyt-popup-title"
      className="fixed inset-0 z-[999] flex items-center justify-center p-4"
    >
      {/* 背景遮罩 */}
      <div
        className={`absolute inset-0 bg-black/45 transition-opacity duration-300 ${
          entered ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={handleClose}
      />

      {/* 彈窗本體 */}
      <div
        className={`relative w-full max-w-[400px] bg-[#FFFDF8] border border-[#E1D2AC] rounded-2xl shadow-2xl overflow-hidden transition-all duration-300 ease-out ${
          entered
            ? 'opacity-100 translate-y-0 scale-100'
            : 'opacity-0 translate-y-4 scale-95'
        }`}
      >
        <button
          onClick={handleClose}
          aria-label="關閉"
          className="absolute top-2.5 right-2.5 z-10 w-8 h-8 rounded-full bg-white/85 hover:bg-white text-[#3A2E20] flex items-center justify-center text-lg leading-none transition"
        >
          ×
        </button>

        {/* 產品圖片區 */}
        <div className="relative bg-[#F0E7D3] aspect-[4/3] flex items-center justify-center overflow-hidden">
          <span className="absolute top-3 left-3 bg-[#A6392A] text-white text-[11px] font-bold tracking-wide px-3 py-1 rounded-full">
            為你推薦
          </span>
          <img
            src={productImage}
            alt={productName}
            className="w-full h-full object-cover"
          />
        </div>

        <div className="p-6 text-center">
          <p className="text-[#C69A46] text-[11.5px] tracking-[0.24em] font-bold mb-2">
            {eyebrow}
          </p>
          <h3
            id="fyt-popup-title"
            className="font-serif text-xl font-bold text-[#3A2E20] mb-1.5 leading-snug"
          >
            {tagline}
          </h3>
          <p className="text-[13px] text-[#7A6A54] mb-4">{productName}</p>

          <ul className="text-left space-y-2 mb-5">
            {benefits.map((b) => (
              <li
                key={b}
                className="flex items-start gap-2 text-[13.5px] text-[#3A2E20] leading-relaxed"
              >
                <span className="text-[#A6392A] font-bold mt-[1px]">✓</span>
                <span>{b}</span>
              </li>
            ))}
          </ul>

          {price ? (
            <p className="text-[#A6392A] font-bold text-lg mb-4">{price}</p>
          ) : null}

          <a
            href={ctaHref}
            className="block w-full bg-[#A6392A] hover:bg-[#832C20] text-white font-bold text-sm rounded-full py-3 transition mb-3"
          >
            {ctaText}
          </a>

          <button
            onClick={handleClose}
            className="block mx-auto text-[12.5px] text-[#7A6A54] hover:text-[#3A2E20] underline underline-offset-2 mb-4 transition"
          >
            下次再說
          </button>

          <label className="flex items-center justify-center gap-2 text-[11.5px] text-[#7A6A54] cursor-pointer select-none">
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              className="w-3.5 h-3.5 accent-[#A6392A]"
            />
            不再提醒我
          </label>
        </div>
      </div>
    </div>
  );
}
